import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type {
  ApprovalFormData,
  ApprovalDecision,
  QCInspection,
  QCCheckpointResult,
  QCDrawing,
} from "types/qc.types";

// Mock data for demo purposes
const mockInspections: QCInspection[] = [
  {
    id: "insp-001",
    productId: "prod-001",
    productName: "DW-4872-A Precast Double Wall Panel",
    companyId: "demo",
    department: "Production Line 1",
    stage: "pre_pour",
    overallStatus: "pass",
    status: "pending_approval",
    notes: "All checkpoints passed. Panel ready for pour.",
    inspectedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    inspector: { firstName: "John", lastName: "Smith" },
  },
  {
    id: "insp-002",
    productId: "prod-002",
    productName: "SW-3648-B Single Wall Panel",
    companyId: "demo",
    department: "Production Line 2",
    stage: "post_pour",
    overallStatus: "needs_review",
    status: "pending_approval",
    notes: "Minor surface imperfection noted on east face. Recommend review before shipping.",
    inspectedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    inspector: { firstName: "Sarah", lastName: "Johnson" },
  },
  {
    id: "insp-003",
    productId: "prod-003",
    productName: "CB-2424-C Column Base",
    companyId: "demo",
    department: "Production Line 1",
    stage: "post_pour",
    overallStatus: "fail",
    status: "pending_approval",
    notes: "Dimensions out of tolerance. Requires rework or NCR.",
    inspectedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    inspector: { firstName: "Mike", lastName: "Davis" },
  },
] as QCInspection[];

const mockCheckpointResults: Record<string, QCCheckpointResult[]> = {
  "insp-001": [
    { id: "cr-001", inspectionId: "insp-001", checkpointId: "form-condition", checkpointName: "Form Condition", passFailResult: "pass", notes: "Clean and ready" },
    { id: "cr-002", inspectionId: "insp-001", checkpointId: "form-joints", checkpointName: "Form Joints", passFailResult: "pass", notes: "" },
    { id: "cr-003", inspectionId: "insp-001", checkpointId: "release-agent", checkpointName: "Release Agent", passFailResult: "pass", notes: "Evenly applied" },
    { id: "cr-004", inspectionId: "insp-001", checkpointId: "reinforcing-design", checkpointName: "Reinforcing Design", passFailResult: "pass", notes: "Matches drawings" },
    { id: "cr-005", inspectionId: "insp-001", checkpointId: "reinforcing-spacing", checkpointName: "Reinforcing Spacing", passFailResult: "pass", notes: "" },
    { id: "cr-006", inspectionId: "insp-001", checkpointId: "lifting-devices", checkpointName: "Lifting Devices", passFailResult: "pass", notes: "Properly positioned" },
  ] as QCCheckpointResult[],
  "insp-002": [
    { id: "cr-101", inspectionId: "insp-002", checkpointId: "surface-finish", checkpointName: "Surface Finish", passFailResult: "pass", notes: "Minor blemish noted" },
    { id: "cr-102", inspectionId: "insp-002", checkpointId: "cracks", checkpointName: "Cracks", passFailResult: "pass", notes: "None visible" },
    { id: "cr-103", inspectionId: "insp-002", checkpointId: "honeycombing", checkpointName: "Honeycombing", passFailResult: "pass", notes: "" },
    { id: "cr-104", inspectionId: "insp-002", checkpointId: "product-length", checkpointName: "Product Length", measurementValue: 36.25, unit: "inches", notes: "Within tolerance" },
    { id: "cr-105", inspectionId: "insp-002", checkpointId: "product-width", checkpointName: "Product Width", measurementValue: 48.0, unit: "inches", notes: "" },
    { id: "cr-106", inspectionId: "insp-002", checkpointId: "bowing", checkpointName: "Bowing", numericRating: 3, notes: "Slight bow - needs review" },
  ] as QCCheckpointResult[],
  "insp-003": [
    { id: "cr-201", inspectionId: "insp-003", checkpointId: "surface-finish", checkpointName: "Surface Finish", passFailResult: "pass", notes: "" },
    { id: "cr-202", inspectionId: "insp-003", checkpointId: "cracks", checkpointName: "Cracks", passFailResult: "pass", notes: "" },
    { id: "cr-203", inspectionId: "insp-003", checkpointId: "product-length", checkpointName: "Product Length", measurementValue: 24.75, unit: "inches", notes: "OUT OF TOLERANCE - spec is 24.00" },
    { id: "cr-204", inspectionId: "insp-003", checkpointId: "product-width", checkpointName: "Product Width", measurementValue: 24.5, unit: "inches", notes: "OUT OF TOLERANCE" },
    { id: "cr-205", inspectionId: "insp-003", checkpointId: "squareness", checkpointName: "Squareness", passFailResult: "fail", notes: "1/4 inch out of square" },
    { id: "cr-206", inspectionId: "insp-003", checkpointId: "lifting-devices", checkpointName: "Lifting Devices", passFailResult: "pass", notes: "" },
  ] as QCCheckpointResult[],
};

const mockDocuments: QCDrawing[] = [
  { id: "doc-001", productId: "prod-001", name: "DW-4872-A Shop Drawing", documentType: "Shop Drawing", version: "2.1", fileUrl: "#" },
  { id: "doc-002", productId: "prod-001", name: "DW-4872-A Reinforcing Detail", documentType: "Rebar Schedule", version: "1.0", fileUrl: "#" },
  { id: "doc-003", productId: "prod-002", name: "SW-3648-B Production Drawing", documentType: "Shop Drawing", version: "1.2", fileUrl: "#" },
] as QCDrawing[];

/**
 * QC Approval Workflow Store
 * Manages the multi-step approval process for inspections
 */
class QCApprovalStore {
  rootStore: RootStore;

  // Wizard State
  activeStep = 0;
  steps = ["Select Inspection", "Review Details", "Review Documents", "Decision"];

  // Form Data
  formData: ApprovalFormData = {
    inspectionId: null,
    inspection: null,
    checkpoints: [],
    documents: [],
    allDocumentsReviewed: false,
    decision: null,
    approvalNotes: "",
    rejectionReason: "",
    requiresFollowUp: false,
    followUpNotes: "",
  };

  // UI State
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Demo mode flag
  useMockData = true;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ============= Step Navigation =============

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  goToStep = (step: number) => {
    if (step >= 0 && step < this.steps.length) {
      // Can only go back or to current step
      if (step <= this.activeStep) {
        this.activeStep = step;
      }
    }
  };

  // ============= Validation =============

  validateCurrentStep = (showError = true): boolean => {
    switch (this.activeStep) {
      case 0: // Select Inspection
        if (!this.formData.inspection) {
          if (showError) this.error = "Please select an inspection to review";
          return false;
        }
        return true;

      case 1: // Review Details
        // Auto-pass - just viewing
        return true;

      case 2: // Review Documents
        if (!this.formData.allDocumentsReviewed && this.formData.documents.length > 0) {
          if (showError) this.error = "Please review all documents before proceeding";
          return false;
        }
        return true;

      case 3: // Decision
        if (!this.formData.decision) {
          if (showError) this.error = "Please select approve or reject";
          return false;
        }
        if (this.formData.decision === "rejected" && !this.formData.rejectionReason.trim()) {
          if (showError) this.error = "Please provide a reason for rejection";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // ============= Actions =============

  selectInspection = async (inspectionId: string) => {
    this.loading = true;
    this.error = null;

    try {
      // Use mock data for demo
      if (this.useMockData) {
        const inspection = mockInspections.find((i) => i.id === inspectionId);
        if (!inspection) {
          throw new Error("Inspection not found");
        }

        runInAction(() => {
          this.formData.inspectionId = inspectionId;
          this.formData.inspection = inspection;
          this.formData.checkpoints = mockCheckpointResults[inspectionId] || [];
          this.formData.documents = mockDocuments.filter((d) => d.productId === inspection.productId);
          this.loading = false;
        });
        return;
      }

      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const inspection = inspectionsStore?.entities?.find(
        (i: QCInspection) => i.id === inspectionId
      );

      if (!inspection) {
        throw new Error("Inspection not found");
      }

      // Load related checkpoints
      const checkpointsStore = this.rootStore["qc-checkpoint-resultsStore"] as ServiceStore;
      if (checkpointsStore) {
        await checkpointsStore.find({ query: { inspectionId } });
      }

      // Load related documents/drawings
      const drawingsStore = this.rootStore["qc-drawingsStore"] as ServiceStore;
      if (drawingsStore) {
        await drawingsStore.find({ query: { productId: inspection.productId } });
      }

      runInAction(() => {
        this.formData.inspectionId = inspectionId;
        this.formData.inspection = inspection;
        this.formData.checkpoints = (checkpointsStore?.entities || []).filter(
          (c: QCCheckpointResult) => c.inspectionId === inspectionId
        );
        this.formData.documents = (drawingsStore?.entities || []).filter(
          (d: QCDrawing) => d.productId === inspection.productId
        );
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load inspection";
        this.loading = false;
      });
    }
  };

  setDecision = (decision: ApprovalDecision) => {
    this.formData.decision = decision;
    if (decision === "approved") {
      this.formData.rejectionReason = "";
    }
  };

  setApprovalNotes = (notes: string) => {
    this.formData.approvalNotes = notes;
  };

  setRejectionReason = (reason: string) => {
    this.formData.rejectionReason = reason;
  };

  setRequiresFollowUp = (requires: boolean) => {
    this.formData.requiresFollowUp = requires;
  };

  setFollowUpNotes = (notes: string) => {
    this.formData.followUpNotes = notes;
  };

  markDocumentsReviewed = () => {
    this.formData.allDocumentsReviewed = true;
  };

  // ============= Submit =============

  submitDecision = async () => {
    if (!this.validateCurrentStep()) return;

    this.loading = true;
    this.error = null;

    try {
      const isApproved = this.formData.decision === "approved";

      // Demo mode - just show success without API calls
      if (this.useMockData) {
        // Simulate a brief delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Remove the inspection from mock data (simulate status change)
        const inspectionIndex = mockInspections.findIndex((i) => i.id === this.formData.inspectionId);
        if (inspectionIndex !== -1) {
          mockInspections[inspectionIndex].status = isApproved ? "approved" : "rejected";
        }

        runInAction(() => {
          this.success = isApproved
            ? "Inspection approved successfully!"
            : "Inspection rejected - inspector will be notified";
          this.loading = false;
        });

        // Navigate back after delay
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("QCManagerDashboard");
        }, 2000);
        return;
      }

      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const approvalsStore = this.rootStore["qc-approvalsStore"] as ServiceStore;
      const user = (this.rootStore as any).authStore?.user;

      // Update the inspection status
      await inspectionsStore.patch(this.formData.inspectionId, {
        status: isApproved ? "approved" : "rejected",
        approvedBy: user?.id,
        approvedAt: new Date().toISOString(),
        approvalNotes: this.formData.approvalNotes,
        rejectionReason: this.formData.rejectionReason,
      });

      // Create approval record
      await approvalsStore.create({
        inspectionId: this.formData.inspectionId,
        decision: this.formData.decision,
        approvedBy: user?.id,
        approvedAt: new Date().toISOString(),
        notes: this.formData.approvalNotes,
        rejectionReason: this.formData.rejectionReason,
        requiresFollowUp: this.formData.requiresFollowUp,
        followUpNotes: this.formData.followUpNotes,
      });

      runInAction(() => {
        this.success = isApproved
          ? "Inspection approved successfully!"
          : "Inspection rejected - inspector will be notified";
        this.loading = false;
      });

      // Navigate back after delay
      setTimeout(() => {
        this.reset();
        this.rootStore.routerStore.goTo("QCManagerDashboard");
      }, 2000);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit decision";
        this.loading = false;
      });
    }
  };

  // ============= Computed Values =============

  get canProceed(): boolean {
    return this.validateCurrentStep(false);
  }

  get isLastStep(): boolean {
    return this.activeStep === this.steps.length - 1;
  }

  get hasFailedCheckpoints(): boolean {
    return this.formData.checkpoints.some(
      (c: QCCheckpointResult) =>
        c.passFailResult === "fail" || (c.numericRating !== undefined && c.numericRating <= 2)
    );
  }

  get pendingInspections(): QCInspection[] {
    // Use mock data for demo
    if (this.useMockData) {
      return mockInspections.filter((i) => i.status === "pending_approval");
    }

    const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
    if (!inspectionsStore?.entities) return [];
    return inspectionsStore.entities.filter(
      (i: QCInspection) => i.status === "pending_approval"
    );
  }

  // ============= Reset =============

  reset = () => {
    this.activeStep = 0;
    this.formData = {
      inspectionId: null,
      inspection: null,
      checkpoints: [],
      documents: [],
      allDocumentsReviewed: false,
      decision: null,
      approvalNotes: "",
      rejectionReason: "",
      requiresFollowUp: false,
      followUpNotes: "",
    };
    this.loading = false;
    this.error = null;
    this.success = null;
  };
}

export default QCApprovalStore;
